import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { getErrorMessage } from '../types/api'

interface UpdateJobDto {
  name?: string
  description?: string
  clientName?: string
  clientEmail?: string
  clientPhone?: string
  jobSiteAddress?: string
  latitude?: number
  longitude?: number
  geofenceRadius?: number
  startDate?: string
  endDate?: string
  estimatedBudget?: number
  status?: string
  assignedWorkerIds?: string[]
}

interface JobDetail {
  id: number
  name: string
  description: string
  clientName: string
  clientEmail: string
  clientPhone: string
  jobSiteAddress: string
  latitude: number
  longitude: number
  geofenceRadius: number
  startDate: string
  endDate?: string
  estimatedBudget: number
  status: string
  companyId: number
  companyName: string
  createdAt: string
  assignedWorkers: Array<{
    userId: string
    firstName: string
    lastName: string
    email: string
    role: string
  }>
}

interface Worker {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
}

const jobStatuses = ['Planning', 'Active', 'OnHold', 'Completed', 'Cancelled']

export function JobEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<UpdateJobDto>({})
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Only Admin and ProjectManager can edit jobs
  const canEditJob = user && (user.role === 'Admin' || user.role === 'ProjectManager')

  // Fetch job details
  const { data: job, isLoading: jobLoading, error: jobError } = useQuery({
    queryKey: ['job', id],
    queryFn: async (): Promise<JobDetail> => {
      const response = await apiClient.get(`/jobs/${id}`)
      return response.data
    },
    enabled: !!id
  })

  // Fetch available workers
  const { data: workers = [] } = useQuery<Worker[]>({
    queryKey: ['workers'],
    queryFn: async (): Promise<Worker[]> => {
      const response = await apiClient.get('/users/workers')
      return response.data
    },
    enabled: !!canEditJob
  })

  // Initialize form data when job is loaded
  useEffect(() => {
    if (job) {
      setFormData({
        name: job.name,
        description: job.description,
        clientName: job.clientName,
        clientEmail: job.clientEmail,
        clientPhone: job.clientPhone,
        jobSiteAddress: job.jobSiteAddress,
        latitude: job.latitude,
        longitude: job.longitude,
        geofenceRadius: job.geofenceRadius,
        startDate: job.startDate.split('T')[0],
        endDate: job.endDate ? job.endDate.split('T')[0] : '',
        estimatedBudget: job.estimatedBudget,
        status: job.status,
        assignedWorkerIds: job.assignedWorkers.map(w => w.userId)
      })
    }
  }, [job])

  const updateJobMutation = useMutation({
    mutationFn: async (data: UpdateJobDto) => {
      // Convert string dates to ISO format for backend
      const payload = {
        ...data,
        startDate: data.startDate ? new Date(data.startDate).toISOString() : undefined,
        endDate: data.endDate ? new Date(data.endDate).toISOString() : undefined
      }
      const response = await apiClient.put(`/jobs/${id}`, payload)
      return response.data
    },
    onSuccess: async () => {
      // Invalidate and refetch job data to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['job', id] })
      // Also invalidate jobs list to update any summary data
      await queryClient.invalidateQueries({ queryKey: ['jobs'] })
      navigate(`/jobs/${id}`)
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { data?: { errors?: Record<string, string>, message?: string } } }
        if (apiError.response?.data?.errors) {
          setErrors(apiError.response.data.errors)
        } else if (apiError.response?.data?.message) {
          setErrors({ general: apiError.response.data.message })
        }
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    
    // Basic validation
    const newErrors: Record<string, string> = {}
    
    if (formData.name && formData.name.length > 300) {
      newErrors.name = 'Job name must be 300 characters or less'
    }
    if (formData.clientName && formData.clientName.length > 200) {
      newErrors.clientName = 'Client name must be 200 characters or less'
    }
    if (formData.jobSiteAddress && formData.jobSiteAddress.length > 500) {
      newErrors.jobSiteAddress = 'Job site address must be 500 characters or less'
    }
    if (formData.description && formData.description.length > 2000) {
      newErrors.description = 'Description must be 2000 characters or less'
    }
    if (formData.clientEmail && formData.clientEmail.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.clientEmail)) {
        newErrors.clientEmail = 'Please enter a valid email address'
      }
    }
    if (formData.clientPhone && formData.clientPhone.length > 20) {
      newErrors.clientPhone = 'Phone number must be 20 characters or less'
    }
    if (formData.estimatedBudget !== undefined && formData.estimatedBudget < 0) {
      newErrors.estimatedBudget = 'Budget must be positive'
    }
    if (formData.geofenceRadius !== undefined && (formData.geofenceRadius < 10 || formData.geofenceRadius > 1000)) {
      newErrors.geofenceRadius = 'Geofence radius must be between 10 and 1000 meters'
    }
    if (formData.latitude !== undefined && (formData.latitude < -90 || formData.latitude > 90)) {
      newErrors.latitude = 'Latitude must be between -90 and 90'
    }
    if (formData.longitude !== undefined && (formData.longitude < -180 || formData.longitude > 180)) {
      newErrors.longitude = 'Longitude must be between -180 and 180'
    }
    if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) {
      newErrors.endDate = 'End date must be after start date'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    updateJobMutation.mutate(formData)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  const handleWorkerToggle = (workerId: string) => {
    setFormData(prev => ({
      ...prev,
      assignedWorkerIds: prev.assignedWorkerIds?.includes(workerId)
        ? prev.assignedWorkerIds.filter(id => id !== workerId)
        : [...(prev.assignedWorkerIds || []), workerId]
    }))
  }

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser')
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude
        const lng = position.coords.longitude
        
        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng
        }))

        // Try to get address from coordinates
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
          )
          const data = await response.json()
          
          if (data && data.display_name) {
            setFormData(prev => ({
              ...prev,
              jobSiteAddress: data.display_name
            }))
          }
        } catch (error) {
          console.error('Reverse geocoding failed:', error)
        }
      },
      (error) => {
        console.error('Error getting location:', error)
        alert('Unable to get your location. Please enter the address manually.')
      }
    )
  }

  if (!canEditJob) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">You do not have permission to edit jobs</p>
          <button 
            onClick={() => navigate('/jobs')}
            className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    )
  }

  if (jobLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading job details...</p>
        </div>
      </div>
    )
  }

  if (jobError || !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{getErrorMessage(jobError, 'Failed to load job details')}</p>
          <button 
            onClick={() => navigate('/jobs')}
            className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Edit Job</h1>
          <p className="mt-2 text-sm text-gray-600">Update the job information below</p>
        </div>

        {errors.general && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{errors.general}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 bg-white shadow rounded-lg p-6">
          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Job Name *
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  name="status"
                  id="status"
                  value={formData.status || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  {jobStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  id="description"
                  rows={3}
                  value={formData.description || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Client Information</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="clientName" className="block text-sm font-medium text-gray-700">
                  Client Name *
                </label>
                <input
                  type="text"
                  name="clientName"
                  id="clientName"
                  value={formData.clientName || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {errors.clientName && <p className="mt-1 text-sm text-red-600">{errors.clientName}</p>}
              </div>

              <div>
                <label htmlFor="clientEmail" className="block text-sm font-medium text-gray-700">
                  Client Email
                </label>
                <input
                  type="email"
                  name="clientEmail"
                  id="clientEmail"
                  value={formData.clientEmail || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {errors.clientEmail && <p className="mt-1 text-sm text-red-600">{errors.clientEmail}</p>}
              </div>

              <div>
                <label htmlFor="clientPhone" className="block text-sm font-medium text-gray-700">
                  Client Phone
                </label>
                <input
                  type="tel"
                  name="clientPhone"
                  id="clientPhone"
                  value={formData.clientPhone || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {errors.clientPhone && <p className="mt-1 text-sm text-red-600">{errors.clientPhone}</p>}
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Location Information</h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="jobSiteAddress" className="block text-sm font-medium text-gray-700">
                  Job Site Address *
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    name="jobSiteAddress"
                    id="jobSiteAddress"
                    value={formData.jobSiteAddress || ''}
                    onChange={handleChange}
                    className="flex-1 block w-full border-gray-300 rounded-none rounded-l-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleGetCurrentLocation}
                    className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm hover:bg-gray-100"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                </div>
                {errors.jobSiteAddress && <p className="mt-1 text-sm text-red-600">{errors.jobSiteAddress}</p>}
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label htmlFor="latitude" className="block text-sm font-medium text-gray-700">
                    Latitude
                  </label>
                  <input
                    type="number"
                    name="latitude"
                    id="latitude"
                    step="0.000001"
                    value={formData.latitude || 0}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  {errors.latitude && <p className="mt-1 text-sm text-red-600">{errors.latitude}</p>}
                </div>

                <div>
                  <label htmlFor="longitude" className="block text-sm font-medium text-gray-700">
                    Longitude
                  </label>
                  <input
                    type="number"
                    name="longitude"
                    id="longitude"
                    step="0.000001"
                    value={formData.longitude || 0}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  {errors.longitude && <p className="mt-1 text-sm text-red-600">{errors.longitude}</p>}
                </div>

                <div>
                  <label htmlFor="geofenceRadius" className="block text-sm font-medium text-gray-700">
                    Geofence Radius (meters)
                  </label>
                  <input
                    type="number"
                    name="geofenceRadius"
                    id="geofenceRadius"
                    min="10"
                    max="1000"
                    value={formData.geofenceRadius || 100}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  {errors.geofenceRadius && <p className="mt-1 text-sm text-red-600">{errors.geofenceRadius}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Schedule & Budget */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Schedule & Budget</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="startDate"
                  id="startDate"
                  value={formData.startDate || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  id="endDate"
                  value={formData.endDate || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
              </div>

              <div>
                <label htmlFor="estimatedBudget" className="block text-sm font-medium text-gray-700">
                  Estimated Budget ($)
                </label>
                <input
                  type="number"
                  name="estimatedBudget"
                  id="estimatedBudget"
                  min="0"
                  step="0.01"
                  value={formData.estimatedBudget || 0}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {errors.estimatedBudget && <p className="mt-1 text-sm text-red-600">{errors.estimatedBudget}</p>}
              </div>
            </div>
          </div>

          {/* Assigned Workers */}
          {canEditJob && workers.length > 0 && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Assigned Workers</h2>
              <div className="space-y-2">
                {workers.map((worker) => (
                  <label key={worker.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.assignedWorkerIds?.includes(worker.id) || false}
                      onChange={() => handleWorkerToggle(worker.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {worker.firstName} {worker.lastName} ({worker.email}) - {worker.role}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(`/jobs/${id}`)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateJobMutation.isPending}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {updateJobMutation.isPending ? 'Updating...' : 'Update Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}